import React from 'react';
import styled from 'styled-components';
import {withRouter} from 'react-router-dom';

import {getRentalDashboard} from '../../api/rental';
import Layout from './Layout';
import {
  ActionButton,
  Actions,
  Badge,
  Card,
  CardTitle,
  ErrorText,
  Select,
  State,
} from './styles';

const money = value => `${(Number(value || 0) / 100).toLocaleString('ru-RU')} ₽`;

const time = value => {
  if (!value) { return '—'; }
  const date = new Date(value);
  return date.toLocaleString('ru-RU', {day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'});
};

const kindLabels = {
  pickup: 'Выдача',
  return: 'Возврат',
  overdue: 'Просрочен возврат',
};

function DashboardContent({authorization, history}) {
  const [data, setData] = React.useState(null);
  const [locationId, setLocationId] = React.useState('');
  const [state, setState] = React.useState('loading');
  const [error, setError] = React.useState('');

  const load = React.useCallback(async () => {
    if (!authorization) { return; }
    setState('loading');
    setError('');
    try {
      const response = await getRentalDashboard(authorization, locationId || null);
      setData(response);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setState('ready');
    }
  }, [authorization, locationId]);

  React.useEffect(() => { load(); }, [load]);

  const counters = (data && data.counters) || {};
  const operations = (data && data.operations) || [];
  const locations = (data && data.locations) || [];

  const attention = [
    counters.kyc_pending ? {label: 'Документы на проверке', value: counters.kyc_pending, to: '/rental/documents'} : null,
    counters.overdue ? {label: 'Просроченные возвраты', value: counters.overdue, to: '/rental/rentals'} : null,
    counters.with_debt ? {label: 'Аренды с задолженностью', value: counters.with_debt, to: '/rental/rentals'} : null,
    counters.payment_operations_stuck
      ? {label: 'Незакрытые платёжные операции', value: counters.payment_operations_stuck, to: '/rental/rentals'}
      : null,
    counters.vehicles_without_location
      ? {label: 'Автомобили без пункта выдачи', value: counters.vehicles_without_location, to: '/rental/vehicles'}
      : null,
  ].filter(Boolean);

  return (
    <>
      <Toolbar>
        <Select value={locationId} onChange={event => setLocationId(event.target.value)}>
          <option value="">Все пункты выдачи</option>
          {locations.map(location => (
            <option key={location.id} value={location.id}>
              {location.city} · {location.title}
            </option>
          ))}
        </Select>
        <ActionButton onClick={load}>Обновить</ActionButton>
      </Toolbar>

      {error && <ErrorText>{error}</ErrorText>}
      {state === 'loading' && <State>Загрузка…</State>}

      {state !== 'loading' && (
        <>
          <Card>
            <CardTitle>Требует внимания</CardTitle>
            {attention.length === 0 ? (
              <State>Ничего не ждёт действий оператора.</State>
            ) : (
              <AttentionGrid>
                {attention.map(item => (
                  <AttentionItem key={item.label} onClick={() => history.push(item.to)}>
                    <strong>{item.value}</strong>
                    <span>{item.label}</span>
                  </AttentionItem>
                ))}
              </AttentionGrid>
            )}
            {counters.debt_amount > 0 && (
              <State>Общая задолженность клиентов: {money(counters.debt_amount)}</State>
            )}
          </Card>

          <MetricGrid>
            <Metric>
              <MetricValue>{counters.pickups_today || 0}</MetricValue>
              <MetricLabel>Выдачи сегодня</MetricLabel>
            </Metric>
            <Metric>
              <MetricValue>{counters.returns_today || 0}</MetricValue>
              <MetricLabel>Возвраты сегодня</MetricLabel>
            </Metric>
            <Metric>
              <MetricValue>{counters.active || 0}</MetricValue>
              <MetricLabel>Активные аренды</MetricLabel>
            </Metric>
            <Metric>
              <MetricValue>{counters.reserved || 0}</MetricValue>
              <MetricLabel>Забронировано</MetricLabel>
            </Metric>
            <Metric>
              <MetricValue>{counters.awaiting_payment || 0}</MetricValue>
              <MetricLabel>Ожидают оплаты</MetricLabel>
            </Metric>
            <Metric>
              <MetricValue>{counters.inspection || 0}</MetricValue>
              <MetricLabel>На осмотре</MetricLabel>
            </Metric>
            <Metric>
              <MetricValue>
                {counters.vehicles_available || 0}
                <MetricTotal> / {counters.vehicles_total || 0}</MetricTotal>
              </MetricValue>
              <MetricLabel>Автомобилей свободно</MetricLabel>
            </Metric>
            <Metric>
              <MetricValue>{counters.vehicles_maintenance || 0}</MetricValue>
              <MetricLabel>В обслуживании</MetricLabel>
            </Metric>
          </MetricGrid>

          <Card>
            <CardTitle>Сегодня и просроченное</CardTitle>
            {operations.length === 0 ? (
              <State>На сегодня выдач и возвратов нет.</State>
            ) : (
              <OperationList>
                {operations.map(operation => (
                  <OperationRow
                    key={operation.id}
                    overdue={operation.kind === 'overdue'}
                    onClick={() => history.push('/rental/rentals')}
                  >
                    <OperationTime>
                      {operation.kind === 'pickup' ? time(operation.starts_at) : time(operation.ends_at)}
                    </OperationTime>
                    <div>
                      <strong>{kindLabels[operation.kind]}</strong>
                      <div>
                        {operation.brand} {operation.model} · {operation.license_plate}
                      </div>
                      <Muted>
                        {operation.city ? `${operation.city} · ${operation.location_title}` : 'Пункт не указан'}
                        {operation.user_phone ? ` · ${operation.user_phone}` : ''}
                      </Muted>
                    </div>
                    <div>
                      {operation.debt_amount > 0 && <Badge>Долг {money(operation.debt_amount)}</Badge>}
                      {operation.deposit_status === 'held' && <Badge>Депозит {money(operation.deposit_amount)}</Badge>}
                    </div>
                  </OperationRow>
                ))}
              </OperationList>
            )}
          </Card>

          <Card>
            <CardTitle>Пункты выдачи</CardTitle>
            {locations.length === 0 ? (
              <State>
                Пунктов пока нет.{' '}
                <Inline onClick={() => history.push('/rental/locations')}>Создать первый</Inline>
              </State>
            ) : (
              <LocationGrid>
                {locations.map(location => (
                  <LocationCard key={location.id} onClick={() => setLocationId(location.id)}>
                    <strong>{location.city}</strong>
                    <Muted>{location.title}</Muted>
                    <div>
                      {location.vehicles_available} свободно из {location.vehicles_total}
                    </div>
                  </LocationCard>
                ))}
              </LocationGrid>
            )}
          </Card>

          <Actions>
            <ActionButton onClick={() => history.push('/rental/map')}>Карта автомобилей</ActionButton>
            <ActionButton onClick={() => history.push('/rental/rentals')}>Все аренды</ActionButton>
          </Actions>
        </>
      )}
    </>
  );
}

/**
 * Layout передаёт дочернему элементу authorization и role через cloneElement,
 * поэтому содержимое вынесено в отдельный компонент: фрагмент такие props теряет.
 */
function Dashboard() {
  return (
    <Layout
      title="Дашборд проката"
      description="Что требует действия прямо сейчас и что происходит сегодня."
    >
      <DashboardContentWithRouter />
    </Layout>
  );
}

const DashboardContentWithRouter = withRouter(DashboardContent);

const Toolbar = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;
`;

const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 12px;
  margin: 16px 0;
`;

const Metric = styled.div`
  padding: 16px;
  border: 1px solid #e1e5ea;
  border-radius: 10px;
  background: #fff;
`;

const MetricValue = styled.div`
  font-size: 26px;
  font-weight: 700;
  line-height: 1.1;
`;

const MetricTotal = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: #8b949f;
`;

const MetricLabel = styled.div`
  margin-top: 6px;
  font-size: 13px;
  color: #6b7480;
`;

const AttentionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
`;

const AttentionItem = styled.button`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px;
  border: 1px solid #f0c98a;
  border-radius: 10px;
  background: #fdf6e9;
  text-align: left;
  cursor: pointer;

  strong {
    font-size: 22px;
  }

  span {
    font-size: 13px;
    color: #6b7480;
  }
`;

const OperationList = styled.div`
  display: flex;
  flex-direction: column;
`;

const OperationRow = styled.button`
  display: grid;
  grid-template-columns: 88px 1fr auto;
  gap: 12px;
  align-items: center;
  padding: 12px 8px;
  border: 0;
  border-bottom: 1px solid #eef1f4;
  border-left: 3px solid ${props => (props.overdue ? '#e05c5c' : 'transparent')};
  background: transparent;
  text-align: left;
  cursor: pointer;

  &:last-child {
    border-bottom: 0;
  }
`;

const OperationTime = styled.div`
  font-weight: 600;
`;

const Muted = styled.div`
  font-size: 12px;
  color: #8b949f;
`;

const LocationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
`;

const LocationCard = styled.button`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px;
  border: 1px solid #e1e5ea;
  border-radius: 10px;
  background: #fff;
  text-align: left;
  cursor: pointer;
`;

const Inline = styled.button`
  border: 0;
  background: none;
  color: #1a73e8;
  cursor: pointer;
  padding: 0;
`;

export default Dashboard;
